import { lazy, Suspense } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { createBrowserRouter, Navigate, RouteObject } from 'react-router-dom';
import { AppLayout } from '../components/layout/AppLayout';
import { MAINTENANCE_ENABLED } from '../components/layout/Sidebar/navItems';
import { LoginPage } from '../features/auth/LoginPage';
import { LogoutPage } from '../features/auth/LogoutPage';
import { RequireAdmin } from './RequireAdmin';
import { RequireAuth } from './RequireAuth';

const PlantViewPage = lazy(() => import('../features/plant-view/PlantViewPage').then((m) => ({ default: m.PlantViewPage })));
const OperatorDashboardPage = lazy(() =>
  import('../features/operator/OperatorDashboardPage').then((m) => ({ default: m.OperatorDashboardPage })),
);
const TotalizerPage = lazy(() => import('../features/totalizer/TotalizerPage').then((m) => ({ default: m.TotalizerPage })));
const DataLoggerPage = lazy(() => import('../features/logger/DataLoggerPage').then((m) => ({ default: m.DataLoggerPage })));
const NotificationsPage = lazy(() =>
  import('../features/notifications/NotificationsPage').then((m) => ({ default: m.NotificationsPage })),
);
const PerformancePage = lazy(() => import('../features/performance/PerformancePage').then((m) => ({ default: m.PerformancePage })));
const EnergyPage = lazy(() => import('../features/energy/EnergyPage').then((m) => ({ default: m.EnergyPage })));
const ReportsPage = lazy(() => import('../features/reports/ReportsPage').then((m) => ({ default: m.ReportsPage })));
const EquipmentPage = lazy(() => import('../features/equipment/EquipmentPage').then((m) => ({ default: m.EquipmentPage })));
const MaintenancePage = lazy(() => import('../features/maintenance/MaintenancePage').then((m) => ({ default: m.MaintenancePage })));
const UsersPage = lazy(() => import('../features/users/UsersPage').then((m) => ({ default: m.UsersPage })));
const AdminNotifyPage = lazy(() => import('../features/announcements/AdminNotifyPage').then((m) => ({ default: m.AdminNotifyPage })));
const SettingsPage = lazy(() => import('../features/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })));

function PageFallback() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
      <CircularProgress size={24} />
    </Box>
  );
}

function withSuspense(element: JSX.Element) {
  return <Suspense fallback={<PageFallback />}>{element}</Suspense>;
}

/**
 * The route tree. `/login` is public; every other route is wrapped in
 * RequireAuth, which redirects to /login whenever there's no authenticated
 * session (see RequireAuth.tsx and store/auth.store.ts).
 */
export const routes: RouteObject[] = [
  { path: '/login', element: <LoginPage /> },
  { path: '/logout', element: <LogoutPage /> },
  {
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    children: [
      { path: '/', element: <Navigate to="/plant-view" replace /> },
      { path: '/plant-view', element: withSuspense(<PlantViewPage />) },
      { path: '/operator', element: withSuspense(<OperatorDashboardPage />) },
      { path: '/totalizer', element: withSuspense(<TotalizerPage />) },
      // The page used to be the Executive Dashboard at /executive; keep old links and bookmarks working.
      { path: '/executive', element: <Navigate to="/totalizer" replace /> },
      { path: '/logger', element: withSuspense(<DataLoggerPage />) },
      { path: '/notifications', element: withSuspense(<NotificationsPage />) },
      { path: '/performance', element: withSuspense(<PerformancePage />) },
      { path: '/energy', element: withSuspense(<EnergyPage />) },
      { path: '/reports', element: withSuspense(<ReportsPage />) },
      { path: '/equipment', element: withSuspense(<EquipmentPage />) },
      {
        path: '/maintenance',
        element: MAINTENANCE_ENABLED ? withSuspense(<MaintenancePage />) : <Navigate to="/plant-view" replace />,
      },
      { path: '/users', element: <RequireAdmin>{withSuspense(<UsersPage />)}</RequireAdmin> },
      { path: '/admin-notify', element: <RequireAdmin>{withSuspense(<AdminNotifyPage />)}</RequireAdmin> },
      { path: '/settings', element: withSuspense(<SettingsPage />) },
    ],
  },
  { path: '*', element: <Navigate to="/plant-view" replace /> },
];

export const router = createBrowserRouter(routes);
